import React, { FunctionComponent } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import MuiBreadcrumbs from "@mui/material/Breadcrumbs";

import type { ILanguage } from "@ducks/config/types";
import { trackBreadcrumb } from "@analytics";
import type { AppState } from "@store";

interface IBreadcrumbsProps {
  items: IBreadcrumb[];
  language: ILanguage;
}

export interface IBreadcrumb {
  //type = Rewards or Promotion?
  type: string;
  name: string;
  //Currently not using url property because in current rewards/promotions user journey, there is only 1 child in breadcrumb for all cases.
  url?: string;
}

const Breadcrumbs: FunctionComponent<IBreadcrumbsProps> = props => {
  const { items, language } = props;

  const isApp = useSelector((state: AppState) => state.config.app.isApp);

  const parent = {
    lang: "",
    id: ""
  };

  if (items.length > 0 && items[0].type === "REWARD") {
    parent.lang = language.BREADCRUMBS_PARENT_REWARDS;
    parent.id = "breadRewards";
  } else {
    parent.lang = language.BREADCRUMBS_PARENT_PROMOTIONS;
    parent.id = "breadPromotions";
  }
  const location = useLocation();

  return (
    <MuiBreadcrumbs
      separator=">"
      aria-label="breadcrumb"
      sx={({ palette }) => ({
        ".MuiBreadcrumbs-root": { lineHeight: "1.25rem" },
        ".MuiBreadcrumbs-ol": {
          width: "100%",
          fontWeight: "400",
          flexWrap: "nowrap",
          whiteSpace: "nowrap",
          display: "inline-flex",
          color: palette.grey[800]
        },
        ".MuiBreadcrumbs-li": {
          fontSize: "0.875rem",
          "&:last-child": {
            fontWeight: "800",
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            color: palette.grey[800]
          }
        },
        ".MuiBreadcrumbs-separator": {
          color: "#757575",
          fontSize: "0.875rem",
          "&:last-child": {
            color: "#757575",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap"
          }
        }
      })}
    >
      <Link
        to={isApp ? "/?app=1" : location.pathname.includes("/bm") ? "/bm" : "/"}
        onClick={(): void | boolean =>
          items.length > 0 && trackBreadcrumb(items[0].type, 0)
        }
      >
        {/*Parent breadcrumb needs to check if page is rewards or promotions*/}
        {parent.lang}
      </Link>

      {/*If child breadcrumb is not the last child, then it should contain the pathname of its associated page in the Link component.*/}
      {items.map((item, index) =>
        item.url && index + 1 !== items.length ? (
          <Link
            key={index}
            to={item.url}
            onClick={(): void => trackBreadcrumb(item.name, index + 1)}
          >
            {item.name}
          </Link>
        ) : (
          /*If child is last in the breadcrumb trail, then it should not be clickable.*/
          <span key={index}>{item.name}</span>
        )
      )}
    </MuiBreadcrumbs>
  );
};

Breadcrumbs.defaultProps = {
  items: []
};

export default Breadcrumbs;
